import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, CheckCircleIcon, PulseIcon } from '@phosphor-icons/react';
import { MONITOR_PROTOCOL_LABEL, type MonitorHistoryRange, type MonitorStatus } from '@meshalert/contract';
import { ApiError, api } from '../lib/api.ts';
import {
  HISTORY_RANGE_LABEL,
  checkTime,
  historyStartLabel,
  relativeTime,
  uptimePercent,
  viewerTimeZone,
} from '../lib/format.ts';
import { PageHeader } from '../components/Layout.tsx';
import { HistoryRangeTabs, MonitorHistoryChart } from '../components/MonitorHistoryChart.tsx';
import {
  Button,
  EmptyState,
  ErrorState,
  ListSkeleton,
  Panel,
  Skeleton,
  StatTile,
  Tabs,
  Td,
  Th,
  cx,
} from '../components/ui.tsx';
import {
  MONITOR_STATUS_CLASS,
  MONITOR_STATUS_LABEL,
  describeCheck,
  formatInterval,
} from './sources/MonitorsSection.tsx';

/**
 * Ein Monitor mit seinem Verlauf: oben wie verlässlich er im gewählten
 * Zeitraum war, darunter jede einzelne Prüfung. Die Balken beantworten
 * „wann", die Liste „was genau".
 */
export function MonitorPage() {
  const { monitorId = '' } = useParams();
  const [range, setRange] = useState<MonitorHistoryRange>('24h');
  const [filter, setFilter] = useState<'all' | 'failed'>('all');

  const monitor = useQuery({
    queryKey: ['monitors', monitorId],
    queryFn: () => api.monitors.get(monitorId),
    refetchInterval: 30_000,
  });

  const history = useQuery({
    queryKey: ['monitors', monitorId, 'history', range, viewerTimeZone],
    queryFn: () => api.monitors.history(monitorId, { range, tz: viewerTimeZone }),
    refetchInterval: 30_000,
    enabled: monitor.isSuccess,
  });

  const checks = useInfiniteQuery({
    queryKey: ['monitors', monitorId, 'checks', filter],
    queryFn: ({ pageParam }) =>
      api.monitors.checks(monitorId, {
        limit: 25,
        cursor: pageParam,
        ...(filter === 'failed' ? { ok: false } : {}),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    refetchInterval: 30_000,
    enabled: monitor.isSuccess,
  });

  if (monitor.isLoading) {
    return (
      <>
        <Skeleton className="mb-6 h-12 w-72" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[104px]" />
          ))}
        </div>
      </>
    );
  }

  if (monitor.isError || !monitor.data) {
    const missing = monitor.error instanceof ApiError && monitor.error.status === 404;
    return (
      <Panel>
        {missing ? (
          <EmptyState
            icon={<PulseIcon size={26} weight="light" />}
            title="Diesen Monitor gibt es nicht"
            description="Er wurde gelöscht, oder der Link ist unvollständig."
            action={<BackLink />}
          />
        ) : (
          <ErrorState message="Der Monitor konnte nicht geladen werden." onRetry={() => monitor.refetch()} />
        )}
      </Panel>
    );
  }

  const data = monitor.data;
  const config = data.monitor ?? null;
  const state = data.monitor_state ?? null;
  const status: MonitorStatus = state?.status ?? 'unknown';
  const totals = history.data?.totals;
  const rows = checks.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <div className="mb-3">
        <BackLink />
      </div>

      <PageHeader
        title={data.name}
        description={
          config
            ? `${MONITOR_PROTOCOL_LABEL[config.check.protocol]}: ${describeCheck(config.check)}, alle ${formatInterval(config.interval_seconds)}`
            : 'Dieser Monitor hat keine gültige Konfiguration.'
        }
        action={<HistoryRangeTabs value={range} onChange={setRange} />}
      />

      <p className="-mt-3 mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-ink-2">
        <span className={cx('size-2 rounded-full', data.is_active ? MONITOR_STATUS_CLASS[status] : 'bg-ink-3')} />
        <span className="font-medium text-ink">{data.is_active ? MONITOR_STATUS_LABEL[status] : 'Pausiert'}</span>
        <span className="text-ink-3">
          {state?.last_checked_at ? `zuletzt geprüft ${relativeTime(state.last_checked_at)}` : 'noch nie geprüft'}
        </span>
        {status === 'down' && state?.last_error ? (
          <span className="basis-full text-[12px] text-critical">{state.last_error}</span>
        ) : null}
      </p>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="Erreichbarkeit"
          value={totals ? uptimePercent(totals.uptime) : undefined}
          loading={history.isLoading}
          tone={
            totals?.uptime == null ? 'neutral' : totals.uptime < 0.95 ? 'critical' : totals.uptime < 0.99 ? 'warning' : 'neutral'
          }
          note={HISTORY_RANGE_LABEL[range]}
        />
        <StatTile label="Prüfungen" value={totals?.checks} loading={history.isLoading} note="geplant ausgeführt" />
        <StatTile
          label="Fehlgeschlagen"
          value={totals?.failures}
          loading={history.isLoading}
          tone={totals && totals.failures > 0 ? 'critical' : 'neutral'}
          note={config ? `Alarm ab ${config.failure_threshold} in Folge` : undefined}
        />
        <StatTile
          label="Mittlere Antwortzeit"
          value={totals?.avg_duration_ms != null ? `${totals.avg_duration_ms} ms` : undefined}
          loading={history.isLoading}
          note="nur erfolgreiche Prüfungen"
        />
      </div>

      <Panel className="mt-4" title="Verlauf">
        {history.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : history.data ? (
          <MonitorHistoryChart
            buckets={history.data.buckets}
            bucket={history.data.bucket}
            startLabel={historyStartLabel(range, history.data.from)}
            caption={`Prüfungen von ${data.name}, ${HISTORY_RANGE_LABEL[range]}`}
          />
        ) : (
          <ErrorState message="Der Verlauf konnte nicht geladen werden." onRetry={() => history.refetch()} />
        )}
      </Panel>

      <Panel
        className="mt-4"
        title="Prüfungen"
        bodyClassName="p-0"
        action={
          <Tabs
            value={filter}
            onChange={setFilter}
            items={[
              { value: 'all', label: 'Alle' },
              { value: 'failed', label: 'Nur Fehlschläge' },
            ]}
          />
        }
      >
        {checks.isLoading ? (
          <ListSkeleton rows={6} />
        ) : checks.isError ? (
          <ErrorState message="Die Prüfungen konnten nicht geladen werden." onRetry={() => checks.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<CheckCircleIcon size={26} weight="light" />}
            title={filter === 'failed' ? 'Kein Fehlschlag' : 'Noch keine Prüfung'}
            description={
              filter === 'failed'
                ? 'Keine der aufbewahrten Prüfungen ist fehlgeschlagen.'
                : 'Sobald der Scheduler diesen Monitor prüft, steht hier jeder einzelne Lauf.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] border-collapse">
                <thead className="border-b border-line bg-sunken">
                  <tr>
                    <Th>Zeitpunkt</Th>
                    <Th>Ergebnis</Th>
                    <Th className="text-right">Antwortzeit</Th>
                    <Th>Meldung</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-subtle)]">
                  {rows.map((check) => (
                    <tr key={check.id} className="transition-colors hover:bg-hover">
                      <Td className="tnum whitespace-nowrap text-[12px]">{checkTime(check.checked_at)}</Td>
                      <Td className="whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          <span className={cx('size-1.5 rounded-full', check.ok ? 'bg-accent' : 'bg-critical')} />
                          <span className={check.ok ? 'text-ink-2' : 'font-medium text-critical'}>
                            {check.ok ? 'Erfolgreich' : 'Fehlgeschlagen'}
                          </span>
                        </span>
                      </Td>
                      <Td className="tnum text-right whitespace-nowrap text-[12px]">{check.duration_ms} ms</Td>
                      <Td>
                        <span title={check.detail} className="block max-w-[28rem] truncate text-[12px]">
                          {check.detail}
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
              <p className="text-[12px] text-ink-3">
                Einzelne Prüfungen werden nach der Aufbewahrungsfrist gelöscht. Der Verlauf oben bleibt vollständig.
              </p>
              {checks.hasNextPage ? (
                <Button size="sm" disabled={checks.isFetchingNextPage} onClick={() => checks.fetchNextPage()}>
                  {checks.isFetchingNextPage ? 'Lädt' : 'Ältere laden'}
                </Button>
              ) : null}
            </div>
          </>
        )}
      </Panel>
    </>
  );
}

function BackLink() {
  return (
    <Link
      to="/quellen"
      state={{ tab: 'monitors' }}
      className="inline-flex items-center gap-1 text-[12px] text-ink-2 transition-colors hover:text-ink"
    >
      <ArrowLeftIcon size={13} />
      Alle Monitore
    </Link>
  );
}
